/**
 * RoboForm Password Manager
 *
 * Previously filed as undetectable under "randomised markers" — but that was only half
 * right. When RoboForm sees a login form it injects a container whose **id** carries a
 * random numeric suffix, but whose base token — and its **class** — are stable:
 *
 *   <div id="zudZLWqgx2D1YKRAY8bWWuw-9676707985130364" class="zudZLWqgx2D1YKRAY8bWWuw">
 *
 * The token `zudZLWqgx2D1YKRAY8bWWuw` was byte-identical across four independent fresh
 * installs in different ephemeral profiles, so it is derived from something stable (the
 * extension's own identity), not regenerated per session as the original analysis
 * assumed. Only the numeric suffix on the id rotates — the class is a reliable, unique
 * marker, and the id prefix corroborates it.
 *
 * Behavioural: RoboForm only injects the container when a login form is present, so the
 * detector presents one as bait via `probeLoginBait`. Like all behavioural signals it
 * fires only while RoboForm is reacting. Observed on version 9.9.8.0.
 */

import { probeLoginBait } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'pnlccmojcmeohlpggmfnbbiapkmbliob';
const TOKEN = 'zudZLWqgx2D1YKRAY8bWWuw';

export const roboform: ExtensionDefinition = {
  id: ID,
  name: 'RoboForm Password Manager',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 600_000,
  interactive: true,
  notes:
    'Detected behaviourally. RoboForm injects a container on login forms; its id suffix ' +
    'is randomised but the base token (as the class, and the id prefix) is stable across ' +
    'installs. The detector baits it with a login form.',
  signals: [
    {
      id: 'dom-login-bait-stable-token',
      kind: 'dom',
      description:
        `Present a login form and look for RoboForm's injected container — matched by its ` +
        `stable class .${TOKEN} or its stable id prefix ${TOKEN}-*. The token survives ` +
        `across installs; only the numeric id suffix is random.`,
      weight: 1,
      run: () => probeLoginBait(`.${TOKEN}, [id^="${TOKEN}-"]`, { waitMs: 3500 }),
    },
  ],
};
